'use client'
import { PortableTextComponents } from '@portabletext/react'
import { getImageDimensions, SanityImageSource } from '@sanity/asset-utils'
import { TableValue } from '@sanity/table'
import { client } from 'lib/util/client'
import { slugifyHeading } from 'lib/util/slugifyHeading'
import Img from 'next/image'
import { useNextSanityImage } from 'next-sanity-image'
import React from 'react'
import { InView } from 'react-intersection-observer'

import { ArticleReference, ArticleReferenceType } from './ArticleReference'
import { LightBulbIcon } from './icons'
import { LinkCard } from './LinkCard'

const SampleImageComponent = ({
  value,
  isInline,
}: {
  value: SanityImageSource
  isInline: boolean
}) => {
  const [fullScreen, setFullScreen] = React.useState(false)
  const { width, height } = getImageDimensions(value)
  const imageProps = useNextSanityImage(client, value)
  return (
    <Img
      {...imageProps}
      loading="lazy"
      //TODO: Add alt text to images
      alt=""
      onClick={(_e) => setFullScreen(!fullScreen)}
      className={`rounded-lg ${fullScreen
        ? 'fixed left-0 top-0 z-50 h-screen w-screen bg-white bg-opacity-100 object-contain'
        : ''
        }`}
      style={{
        // Display alongside text if image appears inside a block text span
        display: isInline ? 'inline-block' : 'block',

        // Avoid jumping around with aspect-ratio CSS property
        aspectRatio: width / height,
      }}
    />
  )
}

export const getPortableTextComponents = (
  handleInView?: (inView: boolean, entry: IntersectionObserverEntry) => void,
): PortableTextComponents => ({
  block: {
    h3: ({ children }) =>
      Array.isArray(children) && (
        <InView rootMargin="0px 0px -80% 0px" onChange={handleInView}>
          {({ inView, ref, entry }) => (
            <div ref={ref} className=" [&:not(:first-child)]:pt-8">
              <h3
                id={slugifyHeading(children.join())}
                data-slug={slugifyHeading(children.join())}
                className={`mb-2 text-2xl font-semibold`}
              >
                {children}
              </h3>
            </div>
          )}
        </InView>
      ),
    h4: ({ children }) =>
      Array.isArray(children) && (
        <h4
          id={slugifyHeading(children.join())}
          className="mb-2 text-xl font-semibold [&:not(:first-child)]:pt-8"
        >
          {children}
        </h4>
      ),
    normal: ({ children }) => {
      // Function to handle rendering of text with potential line breaks
      const renderText = (text: string, key: number) =>
        text.split('\n').map((line, index, array) => (
          <React.Fragment key={`${key}-${index}`}>
            {line}
            {index < array.length - 1 && <br />}
          </React.Fragment>
        ))

      return (
        <div className="mb-5 font-extralight pre-line">
          {React.Children.map(children, (child, index) =>
            typeof child === 'string' ? renderText(child, index) : child,
          )}
        </div>
      )
    },
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mb-7 ml-8 list-outside">{children}</ul>
    ),
    number: ({ children }) => <ol className="mb-7 ml-6 ">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="text mb-2 list-item list-disc">{children}</li>
    ),
    number: ({ children }) => (
      <li className="mb-2 list-item list-decimal">{children}</li>
    ),
  },
  marks: {
    link: ({ children, value }) => (
      <a
        href={value.href}
        target={value.blank ? '_blank' : undefined}
        rel={value.blank ? 'noopener noreferrer' : undefined}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {children}
      </a>
    ),
    // Add rendering for superscript
    sup: ({ children }) => <sup>{children}</sup>,

    // Add rendering for subscript
    sub: ({ children }) => <sub>{children}</sub>,
    strong: ({ children }) => <div className="font-bold">{children}</div>,
  },
  types: {
    country: ({ value }: { value: { label: string, href: string } }) => {
      return (
        <div className="mb-5">
          <LinkCard
            link={value.href}
            title={value.label}
            description=''
          />
        </div>
      )
    },
    card: ({ value }: { value: { type: string; text: string } }) => {
      const cardTypes = {
        challenge: {
          background: 'bg-red-50',
          text: 'text-red-800',
          border: 'border-red-200',
          iconBg: 'bg-red-800',
          iconLineColor: 'text-red-50',
          icon: null,
        },
        enabler: {
          background: 'bg-green-50',
          text: 'text-green-800',
          border: 'border-green-200',
          iconBg: 'bg-green-800',
          iconLineColor: 'text-green-50',
          icon: <LightBulbIcon />,
        },
      } as const
      if (value.type !== 'challenge' && value.type !== 'enabler') {
        throw new Error(`Card type not supported: ${value.type}`)
      }
      const cardType = cardTypes[value.type]

      return (
        <div
          className={`${cardType.background} ${cardType.border} mb-5 flex gap-4 rounded-lg border p-4`}
        >
          <div
            className={`${cardType.iconBg} ${cardType.iconLineColor} mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full  `}
          >
            {cardType.icon}
          </div>
          <p className={`${cardType.text}`}>{value.text}</p>
        </div>
      )
    },
    image: SampleImageComponent,
    publication: ({ value }) => (
      <a href={value.publicationUrl}>{value.publicationTitle}</a>
    ),
    articleReference: ({ value }: { value: ArticleReferenceType }) => {
      return <ArticleReference value={value} />
    },
    table: ({ value }: { value: TableValue }) => {
      const { rows } = value
      const columns = rows[0].cells

      return (
        <div style={{ position: 'relative', wordBreak: 'break-word' }}>
          <table className={`table-fixed`}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                if (index === 0) return null
                return (
                  <tr key={index}>
                    {row.cells.map((cell, i) => (
                      <td key={index} align={i > 0 ? 'center' : 'left'}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    },
  },
})
